import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { Rider, Order } from '@grandxl/types'

// GPS transmission health. Surfaced in the UI as a banner when not 'ok' so the
// rider knows dispatch can't see them — invisible failure was causing missed jobs.
export type GpsStatus = 'ok' | 'permission_denied' | 'unavailable' | 'timeout' | 'unknown'

interface RiderState {
  rider: Rider | null
  isOnline: boolean
  activeOrder: Order | null
  // Queued broadcast jobs the rider hasn't responded to yet
  pendingJobs: Order[]
  gpsStatus: GpsStatus
  lastGpsSuccessAt: number | null // epoch ms
}

interface RiderActions {
  setRider: (rider: Rider | null) => void
  setOnline: (online: boolean) => void
  setActiveOrder: (order: Order | null) => void
  addPendingJob: (order: Order) => void
  removePendingJob: (orderId: string) => void
  // Reconcile the pending-jobs list against the fresh server response so stale
  // offers (taken by another rider, past the 45-min cutoff) disappear and new
  // ones appear. Prior addPendingJob-only loop never shrank the list.
  reconcilePendingJobs: (serverJobs: Order[]) => void
  clearPendingJobs: () => void
  setGpsStatus: (status: GpsStatus) => void
  markGpsSuccess: () => void
}

export const useRiderStore = create<RiderState & RiderActions>()(
  persist(
    (set, get) => ({
      rider: null,
      isOnline: false,
      activeOrder: null,
      pendingJobs: [],
      gpsStatus: 'unknown',
      lastGpsSuccessAt: null,

      setRider: (rider) => set({ rider, isOnline: rider?.isOnline ?? false }),

      setOnline: (online) => {
        set({ isOnline: online })
        const r = get().rider
        if (r) set({ rider: { ...r, isOnline: online } })
      },

      setActiveOrder: (order) => set({ activeOrder: order }),

      addPendingJob: (order) =>
        set((s) => ({
          pendingJobs: s.pendingJobs.some((o) => o._id === order._id)
            ? s.pendingJobs
            : [order, ...s.pendingJobs],
        })),

      removePendingJob: (orderId) =>
        set((s) => ({ pendingJobs: s.pendingJobs.filter((o) => o._id !== orderId) })),

      reconcilePendingJobs: (serverJobs) =>
        set((s) => {
          // Union by id — prefer the server copy when both exist (fresher data:
          // dispatchRounds, declinedBy, etc.). Jobs in the server list that
          // aren't local get added; jobs in the local list that aren't on the
          // server get dropped (taken, declined by this rider, aged out).
          const serverIds = new Set(serverJobs.map((j) => j._id))
          const kept = s.pendingJobs.filter((j) => serverIds.has(j._id))
          const keptIds = new Set(kept.map((j) => j._id))
          const incoming = serverJobs.filter((j) => !keptIds.has(j._id))
          return { pendingJobs: [...incoming, ...kept] }
        }),

      clearPendingJobs: () => set({ pendingJobs: [] }),

      setGpsStatus: (status) => set({ gpsStatus: status }),
      markGpsSuccess: () => set({ gpsStatus: 'ok', lastGpsSuccessAt: Date.now() }),
    }),
    {
      name: 'gxl-rider-store',
      storage: createJSONStorage(() => localStorage),
      // Only persist activeOrder + isOnline. Pending jobs and GPS status are ephemeral
      // — a stale pending offer after relaunch would be misleading (its 45s window is gone),
      // and GPS status is refreshed on every tick.
      partialize: (s) => ({
        activeOrder: s.activeOrder,
        isOnline:    s.isOnline,
      }),
    },
  ),
)
